import { COUNTRIES } from "../../models/countries";
import { CCard, CCardBody, CCardHeader, CCol, CRow } from "@coreui/react";
import {
  Invoice,
  INVOICE_EXPORT_FIELDS,
  INVOICE_SALE_TYPE_CASH,
  INVOICE_SALE_TYPE_CASH_LABEL,
  INVOICE_SALE_TYPE_CREDIT_LABEL,
  INVOICE_STATUS_APPROVED_BY_SET,
  INVOICE_STATUS_CANCELLATION_FAILED,
  INVOICE_STATUS_CANCELLATION_REQUESTED,
  INVOICE_STATUS_CANCELLED,
  INVOICE_STATUS_CHOICES,
  INVOICE_STATUS_ERROR,
  INVOICE_STATUS_NOMINATED,
  INVOICE_STATUS_RECOVERABLE_ERROR,
  INVOICE_STATUS_RESEND_REQUESTED,
  INVOICE_STATUS_SET_UNKNOWN_ERROR,
  INVOICE_STATUS_UPLOADED_TO_SET,
  formatInvoiceNumber,
} from "../../models/invoice";
import { CLIENT_TYPE_CHOICES } from "../../models/client";
import { TRANSACTION_TYPE_CHOICES } from "../../models/transaction-type";
import { CURRENCY_CHOICES, PYG } from "../../currency/available-currencies";
import { formatToCurrency, formatToPyg } from "../../currency/format";
import { IVA_TYPE_CHOICES } from "../../models/iva";
import { formatUTCDateTime } from "../../utils/dates";

interface InvoiceDetailViewProps {
  invoice: Invoice;
}

const getStatusBadgeClassName = (status?: string): string => {
  if (status === INVOICE_STATUS_UPLOADED_TO_SET) return "badge-warning";
  if (
    status === INVOICE_STATUS_ERROR ||
    status === INVOICE_STATUS_RECOVERABLE_ERROR
  )
    return "badge-danger";
  if (status === INVOICE_STATUS_SET_UNKNOWN_ERROR) return "badge-danger";
  if (status === INVOICE_STATUS_APPROVED_BY_SET) return "badge-success";
  if (status === INVOICE_STATUS_CANCELLATION_REQUESTED) return "badge-light";
  if (status === INVOICE_STATUS_CANCELLED) return "badge-info";
  if (status === INVOICE_STATUS_CANCELLATION_FAILED) return "badge-danger";
  if (status === INVOICE_STATUS_RESEND_REQUESTED) return "badge-warning";
  if (status === INVOICE_STATUS_NOMINATED) return "badge-success";
  return "badge-info";
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <p className="mb-1">
    <strong>{label}:</strong> {children || "-"}
  </p>
);

const InvoiceDetailView: React.FC<InvoiceDetailViewProps> = ({ invoice }) => {
  const currency = invoice.currency || PYG;

  const clientAddress = [
    invoice.clientAddress,
    invoice.clientCityDescription,
    invoice.clientDistrictDescription,
    invoice.clientDepartmentDescription,
  ]
    .filter(Boolean)
    .join(", ");

  const hasSetResponse = invoice.setResponseCode || invoice.setError;

  return (
    <div>
      {/* Documento */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Documento</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            {invoice.id && (
              <CCol md="6">
                <Field label="ID">
                  <strong>#{invoice.id}</strong>
                </Field>
              </CCol>
            )}
            {invoice.invoiceNumber && (
              <CCol md="6">
                <Field label="Número de Factura">
                  {formatInvoiceNumber(invoice)}
                </Field>
              </CCol>
            )}
            <CCol md="6">
              <Field label="Timbrado">{invoice.stampIdentifier}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Establecimiento">{invoice.establishmentCode}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Punto de Expedición">
                {invoice.dispatchPointIdentifier}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Fecha">
                {invoice.invoiceDate
                  ? formatUTCDateTime(new Date(invoice.invoiceDate))
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Estado">
                <span
                  className={`badge badge-pill ${getStatusBadgeClassName(invoice.status)}`}
                  style={{ fontSize: "13px" }}
                >
                  {INVOICE_STATUS_CHOICES.get(invoice.status!) || invoice.status}
                </span>
              </Field>
            </CCol>
            {invoice.cdc && (
              <CCol md="12">
                <Field label="CDC">
                  <code style={{ wordBreak: "break-all" }}>{invoice.cdc}</code>
                </Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Contribuyente Emisor */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Contribuyente</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="RUC">{invoice.taxPayerRuc}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Razón Social">{invoice.taxPayerSocialName}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Nombre de Fantasía">
                {invoice.taxPayerFantasyName}
              </Field>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Cliente */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Cliente</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="Razón Social">{invoice.clientSocialName}</Field>
            </CCol>
            <CCol md="6">
              <Field label="RUC">{invoice.clientRuc}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Tipo">
                {invoice.clientType
                  ? CLIENT_TYPE_CHOICES.get(invoice.clientType)
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Email">{invoice.clientEmail}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Teléfono">{invoice.clientPhoneNumber}</Field>
            </CCol>
            {clientAddress && (
              <CCol md="6">
                <Field label="Dirección">{clientAddress}</Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Condición de Venta */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Condición de Venta</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="Condición">
                {invoice.saleType === INVOICE_SALE_TYPE_CASH
                  ? INVOICE_SALE_TYPE_CASH_LABEL
                  : INVOICE_SALE_TYPE_CREDIT_LABEL}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Tipo de Transacción">
                {invoice.transactionTypeCode
                  ? TRANSACTION_TYPE_CHOICES.get(invoice.transactionTypeCode)
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Moneda">
                {invoice.currency ? CURRENCY_CHOICES.get(invoice.currency) : "-"}
              </Field>
            </CCol>
            {invoice.currency !== PYG && invoice.pygExchangeRate != null && (
              <CCol md="6">
                <Field label="Tipo de Cambio">
                  {formatToPyg(invoice.pygExchangeRate)}
                </Field>
              </CCol>
            )}
            {invoice.saleType !== INVOICE_SALE_TYPE_CASH && invoice.term && (
              <CCol md="6">
                <Field label="Plazo">{invoice.term}</Field>
              </CCol>
            )}
            {invoice.obs && (
              <CCol md="12">
                <Field label="Observaciones">{invoice.obs}</Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {invoice.exportData && (
        <CCard className="mb-3">
          <CCardHeader><strong>Factura de exportación</strong></CCardHeader>
          <CCardBody><CRow>
            {INVOICE_EXPORT_FIELDS.map(([key, label]) => invoice.exportData?.[key] && (
              <CCol md="6" key={key}>
                <Field label={label}>{key === "destinationCountry" ? COUNTRIES.find((country) => country.code === invoice.exportData?.destinationCountry)?.description ?? invoice.exportData[key] : invoice.exportData[key]}</Field>
              </CCol>
            ))}
          </CRow></CCardBody>
        </CCard>
      )}

      {/* DNCP */}
      {invoice.dncpContractCode && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>DNCP</strong>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="6">
                <Field label="Código de Contrato">
                  {invoice.dncpContractCode}
                </Field>
              </CCol>
              <CCol md="6">
                <Field label="Modalidad">
                  {invoice.dncpContractModality}
                </Field>
              </CCol>
              <CCol md="6">
                <Field label="Entidad">{invoice.dncpContractEntity}</Field>
              </CCol>
              <CCol md="6">
                <Field label="Año">{invoice.dncpContractYear}</Field>
              </CCol>
              <CCol md="6">
                <Field label="Secuencia">{invoice.dncpContractSequence}</Field>
              </CCol>
              <CCol md="6">
                <Field label="Fecha">{invoice.dncpContractDate}</Field>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      )}

      {/* Ítems */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Ítems</strong>
        </CCardHeader>
        <CCardBody>
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Descripción</th>
                  <th>Código</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>IVA</th>
                  <th>Descuento</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items ?? []).map((item, index) => (
                  <tr key={item.id ?? index}>
                    <td>{index + 1}</td>
                    <td>{item.description || "-"}</td>
                    <td>{item.code || "-"}</td>
                    <td>{item.quantity ?? "-"}</td>
                    <td>
                      {item.unitAmount != null
                        ? formatToCurrency(item.unitAmount, currency)
                        : "-"}
                    </td>
                    <td>
                      {item.ivaType
                        ? IVA_TYPE_CHOICES.get(item.ivaType) ?? item.ivaType
                        : "-"}
                    </td>
                    <td>
                      {item.discount != null
                        ? formatToCurrency(item.discount, currency)
                        : "-"}
                    </td>
                    <td>
                      {item.totalAmount != null
                        ? formatToCurrency(item.totalAmount, currency)
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CCardBody>
      </CCard>

      {/* Totales */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Totales</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="8" />
            <CCol md="4">
              <Field label="Subtotal (10% IVA)">
                {invoice.amount10Percent != null
                  ? formatToCurrency(invoice.amount10Percent, currency)
                  : "-"}
              </Field>
              <Field label="Subtotal (5% IVA)">
                {invoice.amount5Percent != null
                  ? formatToCurrency(invoice.amount5Percent, currency)
                  : "-"}
              </Field>
              <Field label="Subtotal (Exento)">
                {invoice.amountExempt != null
                  ? formatToCurrency(invoice.amountExempt, currency)
                  : "-"}
              </Field>
              <Field label="IVA 10%">
                {invoice.iva10Percent != null
                  ? formatToCurrency(invoice.iva10Percent, currency)
                  : "-"}
              </Field>
              <Field label="IVA 5%">
                {invoice.iva5Percent != null
                  ? formatToCurrency(invoice.iva5Percent, currency)
                  : "-"}
              </Field>
              {invoice.totalDiscount != null && invoice.totalDiscount > 0 && (
                <Field label="Descuento">
                  {formatToCurrency(invoice.totalDiscount, currency)}
                </Field>
              )}
              <p className="mb-1 mt-2">
                <strong>Total:</strong>{" "}
                <h5 className="d-inline">
                  {invoice.amountAfterDiscount != null ||
                  invoice.amount != null ? (
                    formatToCurrency(
                      invoice.amountAfterDiscount ?? invoice.amount!,
                      currency
                    )
                  ) : (
                    "-"
                  )}
                </h5>
              </p>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Información SET */}
      {hasSetResponse && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>Respuesta SET</strong>
          </CCardHeader>
          <CCardBody>
            <CRow>
              {invoice.setResponseCode && (
                <CCol md="6">
                  <Field label="Código de Respuesta">
                    {invoice.setResponseCode}
                  </Field>
                </CCol>
              )}
              {invoice.setResponseDescription && (
                <CCol md="6">
                  <Field label="Descripción">
                    {invoice.setResponseDescription}
                  </Field>
                </CCol>
              )}
              {invoice.setError && (
                <CCol md="12">
                  <p className="mb-1">
                    <strong>Error:</strong>
                  </p>
                  <pre
                    style={{
                      background: "var(--theme-surface-raised)",
                      padding: "8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {invoice.setError}
                  </pre>
                </CCol>
              )}
            </CRow>
          </CCardBody>
        </CCard>
      )}
    </div>
  );
};

export default InvoiceDetailView;
